import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import api from '../../API';
import { useAuth } from '../../providers/AuthContext';

/**
 * Custom hook for fetching and processing network connection data
 */
export function useNetworkData() {
    const { user } = useAuth();
    
    // Backend returns unified structure grouped by girderNetworkUuid
    // Each object has: girderNetworkUuid, outboundConnection, inboundConnection, networkNode, proxiedNode
    const { data, isLoading, error } = useQuery({ 
        queryKey: ['local-connections'], 
        queryFn: () => api.get('network/connections'),
        retry: false,  // Don't retry on auth errors
        enabled: !!user  // Only fetch when user is logged in
    });

    // Process unified data structure - simple and direct
    const connections = useMemo(() => {
        if (!isLoading && data?.status === 200 && Array.isArray(data.data)) {
            // Backend returns unified structure: array of objects grouped by girderNetworkUuid
            // Each object has: girderNetworkUuid, outboundConnection, inboundConnection, networkNode, proxiedNode
            return data.data;
        }
        return [];
    }, [isLoading, data]);
    
    // Get local node info from a separate endpoint (still needed for some UI elements)
    const { data: localData, isLoading: isLoadingLocal } = useQuery({ 
        queryKey: ['local-info'], 
        queryFn: () => api.get('network/local'),
        retry: false,  // Don't retry on errors
        staleTime: 5 * 60 * 1000, // 5 minutes - network data doesn't change frequently
        refetchOnMount: false, // Don't refetch if data is fresh
        refetchOnWindowFocus: false, // Don't refetch on window focus
    });
    
    // Use new flattened response format directly - memoize to prevent recreation
    const siteInfo = useMemo(() => {
        return localData?.status === 200 && localData?.data ? localData.data : null;
    }, [localData]);
    
    const isHub = siteInfo?.isHub || false;
    const defaultNetworkName = siteInfo?.defaultNetworkName;
    
    // Return flattened data as "local" object
    // Memoize to prevent object recreation on every render (fixes infinite loop)
    const local = useMemo(() => {
        if (!siteInfo) return null;
        return {
            apiUrl: siteInfo.apiUrl,
            isHub: siteInfo.isHub,
            siteName: siteInfo.siteName,
            contactName: siteInfo.contactName,
            contactEmail: siteInfo.contactEmail,
            networkName: siteInfo.networkName,
            // allowProtectedOperations is not in public response, will be undefined
            allowProtectedOperations: undefined
        };
    }, [siteInfo]);
    
    // Extract parent connections only (no parentConnectionId)
    // Deduplicate by connection _id to handle bidirectional connections
    const parentConnections = useMemo(() => {
        const parentConns = [];
        const seenConnectionIds = new Set();
        
        connections.forEach(group => {
            // Check outbound connection
            const outboundConn = group.outboundConnection;
            if (outboundConn && !outboundConn.parentConnectionId) {
                const connId = outboundConn._id;
                if (!seenConnectionIds.has(connId)) {
                    parentConns.push(outboundConn);
                    seenConnectionIds.add(connId);
                }
            }
            
            // Check inbound connection (only if different from outbound)
            const inboundConn = group.inboundConnection;
            if (inboundConn && !inboundConn.parentConnectionId) {
                const connId = inboundConn._id;
                // Only add if different from outbound (not bidirectional)
                if (connId !== outboundConn?._id && !seenConnectionIds.has(connId)) {
                    parentConns.push(inboundConn);
                    seenConnectionIds.add(connId);
                }
            }
        });
        
        return parentConns;
    }, [connections]);
    
    // Filter parent connections by status - memoize to prevent recreation
    const pendingList = useMemo(() => {
        return parentConnections.filter(c => ['initiated', 'unverified', 'awaiting-approval', 'connecting'].includes(c.status));
    }, [parentConnections]);
    
    const connectedList = useMemo(() => {
        return parentConnections.filter(c => c.status === 'connected');
    }, [parentConnections]);
    
    const canceledList = useMemo(() => {
        return parentConnections.filter(c => ['canceled', 'rejected'].includes(c.status));
    }, [parentConnections]);
    
    const terminatedList = useMemo(() => {
        return parentConnections.filter(c => c.status === 'terminated');
    }, [parentConnections]);
    

    return {
        local,
        connections,
        pendingList,
        connectedList,
        canceledList,
        terminatedList,
        isLoading: isLoading || isLoadingLocal,
        defaultNetworkName,
    };
}

